import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import AsyncStorage, { isWeb } from '../storage';
import SecureLS from 'secure-ls';
import { SignInApiError } from '../types/commonTypes';

let NativeModules: any;
if (!isWeb) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  NativeModules = require('react-native').NativeModules;
}

const initialUserState = {
  isAuthenticated: false,
  accountId: null,
  customerId: null,
  accountDetails: null,
  accountDetailsError: null,
  loading: false,
  error: null,
  isBiometricEnabled: false,
  rememberMe: false,
  userCredentials: {},
  biometricRetry: 5,
  statusCode: '',
  userDetails: null,
  availableCards: [],
  isSessionTimeout: false,
  userName: null as string | null,
  productName: null,
  userPermissionAndroidCount: 0,
  passkeyLoading: false,
  passkeyError: null as SignInApiError | null,
  passkeyStatusCode: '',
};

// Initialize SecureLS for web
let secureLS: SecureLS | null = null;
if (isWeb) {
  secureLS = new SecureLS({ encodingType: 'aes' });
}

let authenticatedUser: any = null;
if (isWeb && secureLS) {
  let user = secureLS.get('user');
  authenticatedUser = user && JSON.parse(user);
}

// If SecureLS returns null, try reading from localStorage (fallback)
if (!authenticatedUser && isWeb) {
  secureLS = new SecureLS({ encodingType: 'aes' });
  const storedUser = secureLS.get('user');
  if (storedUser) {
    try {
      authenticatedUser = JSON.parse(storedUser);
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      localStorage.removeItem('user');
    }
  }
}

export const initializeStoredUser = createAsyncThunk(
  'user/initializeStoredUser',
  async () => {
    authenticatedUser = await AsyncStorage.getItem('user');
    return authenticatedUser ? JSON.parse(authenticatedUser) : initialUserState;
  }
);

export const initializeAppStore = createAsyncThunk(
  'user/initializeAppStore',
  async () => {
    const hasLaunched = await AsyncStorage.getItem('hasLaunched');
    const upaCount = (await AsyncStorage.getItem('upa_count')) ?? 0;
    let rememberMe;
    if (hasLaunched === null) {
      const dataFromLegacy =
        await NativeModules.KeychainLoginBridge.getUserName();
      rememberMe = dataFromLegacy.hasRememberMe ?? false;
    } else {
      rememberMe =
        ((await AsyncStorage.getItem('remember_me')) ?? 'false') === 'true';
    }
    const isBiometricEnabled = await AsyncStorage.getItem(
      'is_biometric_enabled'
    );
    const newState = {
      ...initialUserState,
      rememberMe,
      userPermissionAndroidCount: upaCount ?? Number(upaCount),
      isBiometricEnabled: isBiometricEnabled === 'true',
    };
    return newState;
  }
);

const persistedUserName =
  authenticatedUser?.userName ||
  authenticatedUser?.userCredentials?.username ||
  authenticatedUser?.userCredentials?.userName ||
  null;

const initialState = {
  ...initialUserState,
  isAuthenticated: authenticatedUser
    ? authenticatedUser.isAuthenticated
    : false,
  accountId: authenticatedUser ? authenticatedUser?.accountId : null,
  customerId: authenticatedUser ? authenticatedUser?.customerId : null,
  accountDetails: authenticatedUser ? authenticatedUser?.accountDetails : null,
  userName: persistedUserName,
};
console.log('initialState', initialState);
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    signInRequest(state, action) {
      state.loading = true;
      state.error = null;
    },
    signInSuccess(state, action) {
      state.accountId = action.payload?.accountId;
      state.customerId = action.payload?.customerId;
      state.isAuthenticated = action.payload?.authenticated;
      state.userCredentials = action.payload?.data;
      state.productName = action?.payload?.productName;
      state.statusCode = '200';
      state.userName = action.payload?.userName;
      state.loading = false;
      if (isWeb && secureLS) {
        secureLS.set('user', JSON.stringify(state));
      } else {
        AsyncStorage.setItem('user', JSON.stringify(state));
        AsyncStorage.setItem('remember_me', JSON.stringify(state.rememberMe));
      }
    },
    signInFailure(state, action) {
      state.loading = false;
      state.error = action.payload?.error;
      state.statusCode = action.payload?.statusCode;
    },
    passkeySignInRequest(state, action) {
      state.passkeyLoading = true;
      state.passkeyError = null;
      state.passkeyStatusCode = '';
    },
    passkeySignInSuccess(state, action) {
      state.accountId = action.payload?.accountId;
      state.customerId = action.payload?.customerId;
      state.isAuthenticated = action.payload?.authenticated ?? true;
      state.productName = action?.payload?.productName;
      state.userName = action.payload?.userName ?? state.userName;
      state.statusCode = '200';
      state.passkeyLoading = false;
      state.passkeyError = null;
      state.passkeyStatusCode = '200';
      if (isWeb && secureLS) {
        secureLS.set('user', JSON.stringify(state));
      } else {
        AsyncStorage.setItem('user', JSON.stringify(state));
        AsyncStorage.setItem('remember_me', JSON.stringify(state.rememberMe));
      }
    },
    passkeySignInFailure(state, action) {
      state.passkeyLoading = false;
      state.passkeyError = action.payload?.error;
      state.passkeyStatusCode = action.payload?.statusCode;
    },
    resetPasskeyError(state) {
      state.passkeyError = null;
      state.passkeyStatusCode = '';
    },
    accountDetailsFetched(state, action) {
      state.loading = false;
      state.accountDetails = action.payload;
      state.error = null;
      state.accountDetailsError = null;
      if (isWeb && secureLS) {
        secureLS.set('user', JSON.stringify(state));
      } else {
        AsyncStorage.setItem('user', JSON.stringify(state));
      }
    },
    accountDetailsFailed(state, action) {
      state.loading = false;
      state.error = action.payload?.error;
      state.accountDetailsError = action.payload?.error;
    },
    availableCardsRequest(state, action) {
      state.loading = true;
    },
    availableCardsFetched(state, action) {
      state.loading = false;
      state.availableCards = action.payload;
    },
    availableCardsFailed(state, action) {
      state.loading = false;
      state.availableCards = [];
    },
    biometricEnabled(state, action) {
      state.loading = false;
      state.isBiometricEnabled = action.payload;
      if (action.payload && !state.rememberMe) {
        state.rememberMe = true;
        AsyncStorage.setItem('remember_me', JSON.stringify(state.rememberMe));
      }
      if (isWeb && secureLS) {
        secureLS.set('user', state);
      } else {
        AsyncStorage.setItem(
          'is_biometric_enabled',
          JSON.stringify(action.payload)
        );
      }
    },
    saveUserCredentials(state, action) {
      if (isWeb && secureLS) {
        secureLS.set('user', state);
      }
    },
    setRememberMe(state, action) {
      state.rememberMe = action.payload;
      AsyncStorage.setItem('remember_me', JSON.stringify(state.rememberMe));
    },
    setIsLoginLoading(state, action) {
      state.loading = action.payload;
      if (isWeb && secureLS) {
        secureLS.set('user', state);
      }
    },
    setBiometricRetry(state, action) {
      state.biometricRetry = action.payload;
    },
    resetErrorStatus(state) {
      state.error = null;
      state.statusCode = '';
    },
    signOutRequest(state, action) {
      if (isWeb) {
        localStorage.removeItem('hasDismissedOffer');
        localStorage.removeItem('preLoginNotificationBannerVisible');
        localStorage.removeItem('postLoginNotificationBannerVisible');
        localStorage.removeItem('activateCard_remindLater');
        localStorage.removeItem('eStatementPref_dismiss');
        localStorage.removeItem('unLockCardpopup_remindLater');
        localStorage.removeItem('linkPopup_dismiss');
        localStorage.removeItem('hasClickedOffer');
        localStorage.removeItem('hasClickedPopup');
      }
    },
    userLogout(state) {
      state.isAuthenticated = false;
      state.isSessionTimeout = true;
      AsyncStorage.removeItem('user');
      if (isWeb && secureLS) {
        secureLS.remove('user');
        AsyncStorage.removeItem('tokens');
      }
    },
    updateSession(state) {
      state.isSessionTimeout = false;
    },
    resetUser(state) {
      state.accountDetails = null;
    },
    resetUserSlice(state) {
      state.accountDetails = null;
    },
    updateUpacount(state) {
      const count = state.userPermissionAndroidCount + 1;
      AsyncStorage.setItem('upa_count', JSON.stringify(count));
      state.userPermissionAndroidCount = count;
    },
    storeUserName(state, action) {
      try {
        if (isWeb) {
          localStorage.setItem('linkAccount_userName', action.payload);
        }
      } catch (error) {
        console.error('Error storing userName to localStorage:', error);
      }
    },
    retrieveUserName(state) {
      try {
        if (isWeb) {
          const storedUserName = localStorage.getItem('linkAccount_userName');
          if (storedUserName) {
            state.userName = storedUserName;
            localStorage.removeItem('linkAccount_userName');
          }
        }
      } catch (error) {
        console.error('Error retrieving userName from localStorage:', error);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initializeStoredUser.fulfilled, (state, action) => {
      return action.payload;
    });
    // @ts-expect-error
    builder.addCase(initializeAppStore.fulfilled, (state, action) => {
      return action.payload;
    });
  },
});

export const {
  signInRequest,
  signInSuccess,
  signInFailure,
  passkeySignInRequest,
  passkeySignInSuccess,
  passkeySignInFailure,
  resetPasskeyError,
  accountDetailsFetched,
  accountDetailsFailed,
  signOutRequest,
  biometricEnabled,
  saveUserCredentials,
  setBiometricRetry,
  resetErrorStatus,
  setRememberMe,
  setIsLoginLoading,
  availableCardsFetched,
  availableCardsFailed,
  availableCardsRequest,
  resetUser,
  resetUserSlice,
  updateSession,
  userLogout,
  updateUpacount,
  storeUserName,
  retrieveUserName,
} = userSlice.actions;

export default userSlice.reducer;
